import React from 'react';
import './TemplateManagement.css';

const injectPreviewStyles = (html = '') => {
  if (!html) return html;

  const styleTag = `
    <style>
      /* ── Match the live-preview look: centered email on soft bg ── */
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        overflow-x: hidden !important;
        background-color: #f1f5f9 !important;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
      }

      /* Outer full-width wrapper table — stay full-width, just center children */
      body > table[role="presentation"][width="100%"],
      body > table[width="100%"] {
        width: 100% !important;
        max-width: 100% !important;
        table-layout: auto !important;
      }

      /* Inner content table (width="600") — center it like the live preview */
      table[width="600"],
      table[style*="max-width: 600"],
      table[style*="max-width:600"] {
        width: 100% !important;
        max-width: 600px !important;
        margin-left: auto !important;
        margin-right: auto !important;
        table-layout: fixed !important;
      }

      /* All other tables: prevent overflow but don't force full-width */
      table {
        max-width: 100% !important;
        border-collapse: collapse;
      }

      td, th {
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
      }

      /* Images: constrain to their container */
      img {
        max-width: 100% !important;
        height: auto !important;
        display: block !important;
        object-fit: contain !important;
        box-sizing: border-box !important;
      }
      td img {
        max-width: 100% !important;
        height: auto !important;
      }

      /* Footer centering */
      footer, .footer, .email-footer, .template-footer,
      td[style*="background-color: #f8f9fa"],
      td[style*="background-color:#f8f9fa"],
      td[style*="border-top"] {
        text-align: center !important;
      }
      td[style*="background-color: #f8f9fa"] img,
      td[style*="background-color:#f8f9fa"] img,
      footer img, .footer img, .email-footer img, .template-footer img,
      .footer-logo img, .email-footer-logo img {
        display: block !important;
        margin-left: auto !important;
        margin-right: auto !important;
        float: none !important;
      }
      td[style*="background-color: #f8f9fa"] div,
      td[style*="background-color:#f8f9fa"] div {
        text-align: center !important;
      }

      /* Container overflow guard */
      .email-container, body > div {
        max-width: 600px !important;
        margin-left: auto !important;
        margin-right: auto !important;
        overflow: hidden !important;
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
      }
    </style>
  `;

  if (html.includes('</head>')) {
    return html.replace('</head>', `${styleTag}</head>`);
  }
  if (html.includes('<body')) {
    return html.replace(/<body([^>]*)>/i, `<body$1>${styleTag}`);
  }
  return `${styleTag}${html}`;
};

const sanitizePreviewHtml = (html = '') => {
  if (!html) return html;
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+=(["']).*?\1/gi, '')
    .replace(/\son\w+=\S+/gi, '')
    .replace(/(href|src)=(["'])\s*javascript:[^"']*\2/gi, '$1=$2#$2');
};

const TemplatePreview = ({ previewData, onClose, compact = false }) => {
  const previewHtml = injectPreviewStyles(sanitizePreviewHtml(previewData?.htmlPreview));
  const attachments = previewData?.bodyAttachments || [];

  return (
    <div className="emtpl-preview-overlay">
      <div className="emtpl-preview-modal">
        <div className="emtpl-preview-header">
          <div>
            <h3 className="emtpl-preview-title">Email Preview</h3>
            <p className="emtpl-preview-subtitle">
              <strong>Subject:</strong> {previewData.subject}
            </p>
          </div>
          <button className="em-btn em-btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="emtpl-preview-body">
          <iframe
            className="emtpl-preview-iframe"
            srcDoc={previewHtml}
            title="Email Preview"
            sandbox="allow-same-origin"
          />
        </div>
        {/* Attachments section */}
        {attachments.length > 0 && (
          <div style={{
            padding: '12px 20px',
            borderTop: '1px solid #e5e7eb',
            background: '#f8fafc',
            borderBottomLeftRadius: '12px',
            borderBottomRightRadius: '12px',
          }}>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 8px', fontWeight: 600 }}>
              📎 Attachments ({attachments.length})
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {attachments.map((a, idx) => (
                <a
                  key={a.url || idx}
                  href={a.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#334155',
                    textDecoration: 'none',
                    transition: 'border-color 0.2s',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  {a.name}
                  {a.size ? <span style={{ color: '#94a3b8', fontSize: '12px' }}>({(a.size / 1024).toFixed(0)} KB)</span> : null}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplatePreview;
